import { Box, Tooltip } from '@chronosphereio/chrono-ui';
import { formatWithTimeZone, getTimeFormat, TimeFormat } from '@chronosphereio/core';
import { getCommonTimeScale } from '@/core';

interface TimeRangeHoverPointerProps {
  hoveredColIdx: number | undefined;
  numColumns: number | undefined;
  timestamp: number | undefined;
  width: number;
  labelWidth: number;
  timeScale: ReturnType<typeof getCommonTimeScale>;
  timeZone: string | undefined;
  timeFormat: TimeFormat;
  axisTimeRange: { start: Date; end: Date };
}

export const TimeRangeHoverPointer = ({
  hoveredColIdx,
  numColumns,
  timestamp,
  width,
  labelWidth,
  timeScale,
  timeZone,
  timeFormat,
  axisTimeRange,
}: TimeRangeHoverPointerProps) => {
  if (hoveredColIdx === undefined || !numColumns || timestamp === undefined) {
    return null;
  }

  const availableWidth = width - labelWidth;
  const columnWidth = availableWidth / numColumns;
  const columnCenterX = hoveredColIdx * columnWidth + columnWidth / 2;

  const stepMs = timeScale?.stepMs ?? 0;
  const endTimestamp = timestamp + stepMs;

  // Check if the axis time range spans multiple days
  const spansMultipleDays =
    formatWithTimeZone(axisTimeRange.start, 'yyyy-MM-dd', timeZone) !==
    formatWithTimeZone(axisTimeRange.end, 'yyyy-MM-dd', timeZone);

  // Show seconds when step size is less than a minute
  const ONE_MINUTE_MS = 60_000;
  const showSeconds = stepMs < ONE_MINUTE_MS;
  const timeFormatStr = getTimeFormat({ timeFormat, omitSeconds: !showSeconds });
  const dateTimeFormat = spansMultipleDays ? `MMM dd ${timeFormatStr}` : timeFormatStr;

  const startStr = formatWithTimeZone(new Date(timestamp), dateTimeFormat, timeZone);
  const endStr = formatWithTimeZone(new Date(endTimestamp), timeFormatStr, timeZone);
  const timeRangeDisplay = `${startStr} - ${endStr}`;

  return (
    <Tooltip
      open={true}
      placement="bottom"
      Content={timeRangeDisplay}
      componentsProps={{
        tooltip: {
          sx: {
            typography: 'caption',
          },
        },
        popper: {
          sx: {
            pointerEvents: 'none',
            zIndex: 1,
          },
        },
      }}
      {...{ arrow: true }}
    >
      <Box
        style={{
          left: `${columnCenterX}px`,
        }}
        sx={{
          position: 'absolute',
          top: 0,
          width: 0,
          height: 0,
        }}
      />
    </Tooltip>
  );
};
